import React, {useEffect, useState} from "react";
import {Alert, Box, Button, Card, DialogActions, DialogContent} from "@mui/material";
import {
    GetSportEquipmentsDocument,
    SportEquipmentEntity,
    useChangeSportObjectMutation
} from "graphql/graphQlApiHooks";


import {FormikHelpers, FormikProvider, useFormik} from "formik";
import * as Yup from "yup";
import {useSnackbar} from "notistack";

import {LoadingButton} from "@mui/lab";
import {declOfNum} from "helpers/declination";
import {SportEquipmentLocation} from "../../Dialogs/views/SportEquipmentLocation";
import {
    useGetSportEquipmentInputVars
} from "../../../../../../store/reactiveVarsStores/spor-equipmnts/getSportEquipmentsInput";


interface ChangeSportObjectValues {
    sportEquipmentIds: string[];
    sportObjectId: string;
    sportSubZoneId: string;
    sportZoneId: string;
}

interface Props {
    close: () => void;
    equipments: string[];
    equipmentsData?: SportEquipmentEntity[];
}

export const SportEquipmentsRelocate = (rest: Props) => {
    const {close, equipments, equipmentsData} = rest;
    const {enqueueSnackbar} = useSnackbar();
    const {getInput} = useGetSportEquipmentInputVars();

    const [sportEquipment, setSportEquipment] = useState<SportEquipmentEntity | null>(null);

    const [changeSportObject] = useChangeSportObjectMutation();

    const ChangeSportObjectSchema = Yup.object().shape({
        sportObjectId: Yup.string().required("Выберите спортивный объект").nullable(),
        sportZoneId: Yup.string().required("Выберите здание/помещение").nullable(),
        sportSubZoneId: Yup.string().required("Выберите спортзону").nullable()
    });

    const formik = useFormik({
        initialValues: {
            sportEquipmentIds: equipments,
            sportObjectId: "",
            sportZoneId: "",
            sportSubZoneId: ""
        } as ChangeSportObjectValues,
        validationSchema: ChangeSportObjectSchema,
        onSubmit: (
            values: ChangeSportObjectValues,
            {setSubmitting, setErrors}: FormikHelpers<ChangeSportObjectValues>
        ) => {
            // Проверка: запрещаем размещение на складе оборудования со статусом "Используется"
            // Оборудование считается на складе, если sportObjectId выбран, но sportZoneId или sportSubZoneId пустые
            const isMovingToStock = values.sportObjectId && (!values.sportZoneId || !values.sportSubZoneId);
            
            if (isMovingToStock && equipmentsData) {
                const equipmentInUse = equipmentsData.find(eq => eq.inUse === true);
                if (equipmentInUse) {
                    enqueueSnackbar(
                        `Невозможно разместить на складе оборудование "${equipmentInUse.name}" со статусом "Используется". Сначала снимите оборудование с использования.`,
                        {
                            variant: "error"
                        }
                    );
                    setSubmitting(false);
                    return;
                }
            }

            changeSportObject({
                variables: {
                    input: {
                        sportEquipmentIds: equipments,
                        toSportObjectId: values.sportObjectId,
                        toSportZoneId: values.sportZoneId,
                        toSportSubZoneId: values.sportSubZoneId
                    }
                },
                awaitRefetchQueries: true,
                refetchQueries: [
                    {
                        query: GetSportEquipmentsDocument,
                        fetchPolicy: "network-only",
                        variables: {
                            input: getInput()
                        }
                    }
                ]
            })
                .then((res) => {
                    close();
                    enqueueSnackbar(
                        `Обрудование успешно перемещено.`
                    );
                })
                .catch(({message}) => {
                    enqueueSnackbar(
                        message.replace("Unexpected error value: ", "").replace(/"/g, ""),
                        {
                            variant: "error"
                        }
                    );

                })
                .finally(() => setSubmitting(false));
        }
    });

    const {isSubmitting, handleSubmit, touched, errors, setValues, values} = formik;
    
    // Проверка для отключения кнопки "Переместить": если перемещаем на склад и есть оборудование со статусом "Используется"
    // Оборудование считается на складе, если sportZoneId или sportSubZoneId пустые (null/undefined/пустая строка)
    const isMovingToStock = values.sportObjectId && (!values.sportZoneId || !values.sportSubZoneId);
    const hasEquipmentInUse = equipmentsData?.some(eq => eq.inUse === true) || false;
    const isDisabled = isSubmitting || (isMovingToStock && hasEquipmentInUse);

    useEffect(() => {
        if (sportEquipment) {
            setValues(prevState => ({
                ...prevState,
                sportObjectId: sportEquipment?.sportObject?.id || "",
                sportSubZoneId: sportEquipment?.sportSubZone?.id || "",
                sportZoneId: sportEquipment?.sportZone?.id || ""
            } as ChangeSportObjectValues));
        }
    }, [sportEquipment]);

    return (
        <FormikProvider value={formik}>
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                p: 2,
                borderTop: "1px solid",
                borderColor: "divider"
            }}>

                {isMovingToStock && hasEquipmentInUse ? (
                    <Alert severity="error">
                        <>
                            Невозможно переместить на склад оборудование со статусом "Используется". 
                            Выберите спортзону или снимите оборудование с использования.
                        </>
                    </Alert>
                ) : (
                    <Alert severity="info">
                        <>
                            {declOfNum(equipments.length, ["Выбрана", "Выбрано", "Выбрано"])}
                            <b>{equipments.length}</b> {declOfNum(equipments.length, ["единица", "единицы", "единиц"])} оборудования,
                            для перемещения. <br/>
                            Укажите <b>куда хотите переместить</b> выбранное оборудование.
                        </>
                    </Alert>
                )}

            </Box>

            <DialogContent
                dividers
                sx={{
                    bgcolor: "background.default"
                }}
            >
                <Card sx={{p: 3}}>
                    <SportEquipmentLocation
                        vertical
                        location={{
                            sportObject: sportEquipment?.sportObject,
                            sportSubZone: sportEquipment?.sportSubZone,
                            sportZone: sportEquipment?.sportZone
                        }}
                        onChangeLocation={(sportObject, sportZone, sportSubZone) => {
                            setSportEquipment({
                                ...sportEquipment,
                                sportObject,
                                sportZone,
                                sportSubZone
                            });
                            // Обновляем значения в formik для корректной работы проверки isMovingToStock
                            setValues(prevState => ({
                                ...prevState,
                                sportObjectId: sportObject?.id || "",
                                sportZoneId: sportZone?.id || "",
                                sportSubZoneId: sportSubZone?.id || ""
                            } as ChangeSportObjectValues));
                        }}
                    />
                </Card>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={close}
                    color="secondary"
                    size="small"
                >
                    Отмена
                </Button>

                <LoadingButton
                    loading={isSubmitting}
                    disabled={isDisabled}
                    size="small"
                    onClick={() => handleSubmit()}
                    variant="contained"
                >
                    Переместить
                </LoadingButton>
            </DialogActions>
        </FormikProvider>
    )
        ;
};
